import React from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { CommonLink } from '@frontend/pole-ui/lib/components/CommonLink'
import { sendEmailForCallback } from '@pole-api/lib-ifa-communication-api/lib/clients/sendEmailForCallback'
import { useMutation } from '@tanstack/react-query'
import { MAX_LENGTH_NAME_FILED } from '@/components/composed/modals/FinancingCallbackModal/components/FinancingCallbackForm/constants'
import { Button } from '@/components/ui'
import { TextInput } from '@/components/ui/ReactHookFormFields'
import { useData } from '@/logic/data'
import { getLegalDocumentUrl } from '@/logic/legalDocuments/helpers'
import { pushGtmEventOnSubmitCallbackForm } from '@/logic/metrika/financing/gtmHelpers'
import { getUserGtmInfo } from '@/logic/metrika/helpers'
import { getProfile } from '@/logic/profile/reducer'
import type { RootState } from '@/redux/interfaces'
import type { TLegalDocuments } from '@/types/legalDocuments'
import { ELegalDocumentType } from '@/types/legalDocuments'
import { formatLocalizedString } from '@/utils/formatUtils'
import { useDeviceType } from '@/utils/hooks/useDeviceType'
import { FinancingCallbackWrapper } from '../FinancingCallbackWrapper'
import { getRequestBodyForCallbackRequest } from './helpers'
import { messages } from './messages'
import type { IFinancingCallbackForm, IFinancingCallbackFormProps } from './types'
import './styles.scss'

const mapState = (state: RootState) => ({
  userGtmInfo: getUserGtmInfo()(state),
  profile: getProfile()(state),
})

export const FinancingCallbackForm = (props: IFinancingCallbackFormProps) => {
  const { onSuccessCallbackRequest, onErrorCallbackRequest, context } = props

  const { profile, userGtmInfo } = useSelector(mapState)
  const { control, handleSubmit } = useForm<IFinancingCallbackForm>({ defaultValues: { phone: profile.phone } })
  const { data: legalDocuments = [] } = useData<TLegalDocuments>('legalDocuments')
  const linkUrl = getLegalDocumentUrl(legalDocuments, ELegalDocumentType.registrationPolicy)
  const { isMobile } = useDeviceType()

  const { mutateAsync: sendCallbackRequest, isLoading } = useMutation({ mutationFn: sendEmailForCallback })

  const onSubmitForm = async (formData: IFinancingCallbackForm) => {
    try {
      const requestBody = getRequestBodyForCallbackRequest(formData, context)
      await sendCallbackRequest(requestBody)

      pushGtmEventOnSubmitCallbackForm(userGtmInfo, 'success')

      onSuccessCallbackRequest()
    } catch (error) {
      onErrorCallbackRequest()
      pushGtmEventOnSubmitCallbackForm(userGtmInfo, 'fail')
    }
  }

  const onInvalid = () => {
    pushGtmEventOnSubmitCallbackForm(userGtmInfo, 'fail')
  }

  const renderActionButton = () => (
    <Button type="submit" modifiers={['green', 'full-width-always']} loaderProps={{ isVisible: isLoading }} isDisabled={isLoading}>
      {messages.form.actions.submit}
    </Button>
  )

  return (
    <FinancingCallbackWrapper>
      <div className="financingCallbackForm">
        <h4 className="financingCallbackForm__title">{messages.title}</h4>
        <div className="financingCallbackForm__subtitle">{messages.subtitle}</div>

        <form onSubmit={handleSubmit(onSubmitForm, onInvalid)} className="financingCallbackForm__form">
          <div className="financingCallbackForm__fields">
            <TextInput
              control={control}
              name="name"
              placeholder={messages.form.name.placeholder}
              rules={{
                maxLength: {
                  value: MAX_LENGTH_NAME_FILED,
                  message: formatLocalizedString(messages.form.errors.maxLength, { length: MAX_LENGTH_NAME_FILED }),
                },
              }}
            />
            <TextInput
              control={control}
              name="phone"
              type="tel"
              shouldBeTouchedBeforeError={false}
              placeholder={messages.form.phone.placeholder}
              isRequired
              rules={{ required: messages.form.errors.require }}
            />
          </div>

          {!isMobile && renderActionButton()}

          <div className="financingCallbackForm__requiredInfo">{messages.form.requiredInfo}</div>

          <div className="financingCallbackForm__agreement">
            {formatLocalizedString(messages.form.agreement.text, {
              link: (
                <CommonLink url={linkUrl} shouldOpenInNewWindow className="financingCallbackForm__agreementLink underline">
                  {messages.form.agreement.link}
                </CommonLink>
              ),
            })}
          </div>
          {isMobile && renderActionButton()}
        </form>
      </div>
    </FinancingCallbackWrapper>
  )
}
